import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import axios from 'axios';
import { Box, Content } from 'components/modals/common';
import { PrimaryButton, SecondaryButton } from 'components/buttons/v4';
import { useHistory } from 'react-router';
import { getBaseReactUrl } from '../../utils/pathUtils';
import { PageContainer, ContentSection, TitleSection, PageTitle } from 'core/layout';
import styled from 'styled-components';
import { Color } from '../../core';

interface IOAuthPageProps {
  match: { params: { partnerSlug: string } };
  isNewAdminPages?: boolean;
}

const OAuthHeading = styled.h2`
  padding-bottom: 10px;
  border-bottom: 1px solid ${Color.veryLightGray};
`;

const OAuthSectionHeader = styled.div`
  font-weight: bold;
`;

const SecondaryButtonWithMargin = styled(SecondaryButton)<{ $isNewAdminPages?: boolean }>`
  margin-left: 0.5em;
  ${props => props.$isNewAdminPages && `
    background-color: transparent !important;
    border-color: ${Color.mainBlue} !important;
    color: ${Color.mainBlue} !important;
    &:hover {
      background-color: rgba(38, 99, 149, 0.1) !important;
      background: rgba(38, 99, 149, 0.1) !important;
      border-color: ${Color.mainBlue} !important;
      color: ${Color.mainBlue} !important;
    }
  `}
`;

const StyledPrimaryButton = styled(PrimaryButton)<{ $isNewAdminPages?: boolean }>`
  ${props => props.$isNewAdminPages && `
    background-color: ${Color.mainBlue} !important;
    border-color: ${Color.mainBlue} !important;
    color: white !important;
    &:hover {
      background-color: ${Color.darkBlue} !important;
      background: ${Color.darkBlue} !important;
      border-color: ${Color.darkBlue} !important;
    }
  `}
`;

export const OAuthPage = React.memo<IOAuthPageProps>((props) => {
  const { partnerSlug } = props.match.params;
  const { isNewAdminPages } = props;
  const [authData, setAuthData] = React.useState<any>(null);
  const { t } = useTranslation();
  const history = useHistory();

  useEffect(() => {
    axios.get(`/api/v3/partners/${partnerSlug}/oauth2_clients`).then((res: any): void => {
      setAuthData(res.data.data);
    });
  }, [partnerSlug]);

  const getReturnURL = () => {
    let returnURL = window.location.pathname + window.location.search;
    if (isNewAdminPages && !returnURL.includes('tab=oauth')) {
      returnURL += returnURL.includes('?') ? '&tab=oauth' : '?tab=oauth';
    }
    return encodeURIComponent(returnURL);
  };

  const createOAuthClient = () => {
    const returnURL = getReturnURL();
    window.location.href = `${getBaseReactUrl()}/app/nav/partners/${partnerSlug}/oauth/new?returnURL=${returnURL}`;
  };

  const editOAuthClient = (authId) => {
    const returnURL = getReturnURL();
    window.location.href = `${getBaseReactUrl()}/app/nav/partners/${partnerSlug}/oauth/${authId}/edit?returnURL=${returnURL}`;
  };

  const updateSAMLSettings = () => {
    const returnURL = getReturnURL();
    history.push(`/nav/partners/${partnerSlug}/oauth/update_saml_settings?returnURL=${returnURL}`);
  };

  return (
    <PageContainer>
      <TitleSection>
        <PageTitle>{t('OAuth Clients')}</PageTitle>
        <StyledPrimaryButton click={createOAuthClient} $isNewAdminPages={isNewAdminPages}>{t('Add New OAuth Client')}</StyledPrimaryButton>
      </TitleSection>
      <ContentSection>
        {authData &&
          authData.map((client, key) => (
            <Box>
              <Content>
                <OAuthHeading>{client.attributes.name}</OAuthHeading>
                <OAuthSectionHeader>{t('OAuth Key')}</OAuthSectionHeader>
                <p>{client.attributes.key}</p>
                {client.attributes['client-type'] !== 'lti_13' && (
                  <div>
                    <OAuthSectionHeader>{t('OAuth Secret')}</OAuthSectionHeader>
                    <p>{client.attributes.secret}</p>
                  </div>
                )}
                <StyledPrimaryButton click={() => editOAuthClient(client.id)} $isNewAdminPages={isNewAdminPages}>{t('Edit')}</StyledPrimaryButton>

                {client.attributes['client-type'] === 'saml' && (
                  <SecondaryButtonWithMargin click={updateSAMLSettings} $isNewAdminPages={isNewAdminPages}>
                    {t('Update SAML Settings')}
                  </SecondaryButtonWithMargin>
                )}
              </Content>
            </Box>
          ))}
      </ContentSection>
    </PageContainer>
  );
});
